import {
	areItemDatesValid,
	AUTO_SCHEDULING_PERSON_WIDTH,
	createCanvasTimelineDate,
	DRAGGABLE_TYPE,
	getCanvasTimelineDateFromMoment,
	getMomentFromCanvasTimelineDate,
	GROUP_SECTION_WIDTH,
	isSimulationMode,
	ITEM_TYPE,
} from '../canvas-timeline/canvas_timeline_util';
import {getAllocationTotal} from '../../scheduling/project_allocation_logic';
import ProjectAllocationItem from './items/project_allocation_item';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import TaskItem from './items/task_item';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {ESTIMATION_UNIT, PROJECT_STATUS} from '../../../constants';
import ProjectGroupGroup from './groups/project_group_group';
import Util from '../../../forecast-app/shared/util/util';
import ProjectItem from './items/project_item';
import TaskGroup from './groups/task_group';
import PlaceholderGroup from './groups/placeholder_group';
import PhaseGroup from './groups/phase_group';
import PhaseItem from './items/phase_item';
import ProjectSubGroup from './groups/project_sub_group';
import ProjectTeamItem from './items/project_team_item';
import ProjectGroup from './groups/project_group';
import {getRelevantDependencyTaskIdSet} from './projects_scheduling_dependencies';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {addSubTaskToMap} from './projects_scheduling_util';

export const getPhasesMap = data => {
	//from project id to list of phase ids, from phase id to phase
	const projectIdToPhaseIdsMap = new Map(),
		phaseIdToPhaseMap = new Map();
	for (const phase of data.phases) {
		phaseIdToPhaseMap.set(phase.id, phase);
		const phasesInProject = projectIdToPhaseIdsMap.has(phase.projectId) ? projectIdToPhaseIdsMap.get(phase.projectId) : [];
		phasesInProject.push(phase.id);
		projectIdToPhaseIdsMap.set(phase.projectId, phasesInProject);
	}
	return {
		projectIdToPhaseIdsMap,
		phaseIdToPhaseMap,
	};
};

export const getTasksMap = data => {
	//from phase id to list of task ids, from task id to task
	const phaseIdToTaskIdsMap = new Map(),
		taskIdToTaskMap = new Map(),
		parentTaskToSubtaskMap = new Map();

	for (const task of data.tasks) {
		// If subtask add to subtask map
		if (task.parentTaskId) {
			addSubTaskToMap(parentTaskToSubtaskMap, task);
		} else {
			taskIdToTaskMap.set(task.id, task);
			//tasks without phase are added to the map with project id
			const phaseId = task.phaseId === undefined ? task.projectId : task.phaseId;
			const tasksInPhase = phaseIdToTaskIdsMap.has(phaseId) ? phaseIdToTaskIdsMap.get(phaseId) : [];
			tasksInPhase.push(task.id);
			phaseIdToTaskIdsMap.set(phaseId, tasksInPhase);
		}
	}
	return {
		phaseIdToTaskIdsMap,
		taskIdToTaskMap,
		parentTaskToSubtaskMap,
	};
};

export const getProjectPersonsMap = data => {
	//from project id to list of project person ids, from project person id to project person
	const projectIdToProjectPersonMap = new Map(),
		projectIdToPersonIdsMap = new Map(),
		projectIdToPersonsNotInTeamMap = new Map();

	const personMap = data.persons.reduce((map, currentPerson) => {
		map[currentPerson.id] = currentPerson;
		return map;
	}, {});
	for (const projectPerson of data.projectPersons) {
		const person = personMap[projectPerson.personId];
		if (!person || person.clientId || !person.active) continue;
		const projectPersonsInProject = projectIdToProjectPersonMap.has(projectPerson.projectId) ? projectIdToProjectPersonMap.get(projectPerson.projectId) : [];
		projectPersonsInProject.push(projectPerson);
		projectIdToProjectPersonMap.set(projectPerson.projectId, projectPersonsInProject);
		// Popluate the projectIdToPersonIdsMap so we can find the persons who have allocations on the project, but are not on the team
		if (projectIdToPersonIdsMap.has(projectPerson.projectId)) {
			projectIdToPersonIdsMap.get(projectPerson.projectId).push(projectPerson.personId);
		} else {
			projectIdToPersonIdsMap.set(projectPerson.projectId, [projectPerson.personId]);
		}
	}

	// Find the people who are allocated to the project but are not on the team
	//TODO: track time it takes to run for loop
	for (const allocation of data.allocations) {
		const projectId = allocation.projectId;
		if (!projectId) {
			continue;
		}
		if (!projectIdToPersonIdsMap.has(projectId) || !projectIdToPersonIdsMap.get(projectId).includes(allocation.personId)) {
			const person = personMap[allocation.personId];
			if (!person || person.clientId || !person.active) {
				continue;
			}
			if (projectIdToPersonsNotInTeamMap.has(projectId)) {
				if (!projectIdToPersonsNotInTeamMap.get(projectId).find(p => p.id === person.id)) {
					projectIdToPersonsNotInTeamMap.get(projectId).push(person);
				}
			} else {
				projectIdToPersonsNotInTeamMap.set(projectId, [person]);
			}
		}
	}

	return {
		projectIdToProjectPersonMap,
		projectIdToPersonsNotInTeamMap,
	};
};

export const getPersonsMap = data => {
	//from person id to person
	const personIdToPersonMap = new Map();
	for (const person of data.persons) {
		personIdToPersonMap.set(person.id, person);
	}
	return {personIdToPersonMap};
};

const getClientsMap = data => {
	//from client id to client
	const clientIdToClientMap = new Map();
	for (const client of data.clients) {
		clientIdToClientMap.set(client.id, client);
	}
	return {clientIdToClientMap};
};

export const getRolesMap = data => {
	//from role id to role
	const roleIdToRoleMap = new Map();
	for (const role of data.roles) {
		roleIdToRoleMap.set(role.id, role);
	}
	return {roleIdToRoleMap};
};

export const getProjectGroupMaps = (pageComponent, data, props) => {
	const sharedContext = props.sharedContext;
	const projectGroupTimelineGroups = new Map(),
		projectGroupTeamMap = new Map(),
		projectGroupColorMap = new Map(),
		projectGroupStartDateMap = new Map(),
		projectGroupEndDateMap = new Map(),
		projectGroupTimeLeftMap = new Map(),
		projectGroupEstimateForecastMap = new Map();
	for (const projectGroup of data.projectGroups) {
		projectGroupTimelineGroups.set(
			projectGroup.id,
			new ProjectGroupGroup({
				id: projectGroup.id,
				companyProjectGroupId: projectGroup.companyProjectGroupId,
				name: projectGroup.name,
				color: projectGroup.color,
				groups: [],
				isConnectedProject: true,
				hideIfEmpty: !props.isProjectTimeline,
				drawIfAnyChildVisible: true,
				onMouseEnter: sharedContext ? undefined : pageComponent.onProjectGroupMouseEnter.bind(pageComponent, projectGroup),
				onMouseLeave: sharedContext ? undefined : pageComponent.onProjectGroupMouseLeave.bind(pageComponent, projectGroup),
			})
		);
		projectGroupColorMap.set(projectGroup.id, projectGroup.color);
		projectGroupTeamMap.set(projectGroup.id, null);
		projectGroupStartDateMap.set(projectGroup.id, null);
		projectGroupEndDateMap.set(projectGroup.id, null);
		projectGroupTimeLeftMap.set(projectGroup.id, 0);
		projectGroupEstimateForecastMap.set(projectGroup.id, 0);
	}

	return {
		projectGroupTimelineGroups,
		projectGroupTeamMap,
		projectGroupColorMap,
		projectGroupStartDateMap,
		projectGroupEndDateMap,
		projectGroupTimeLeftMap,
		projectGroupEstimateForecastMap,
	};
};

const getProjectById = (id, data) => {
	return data.projects.find(project => project.id === id);
};

const sortProjectGroups = (pageComponent, groups) => {
	const projectGroups = groups.filter(group => !group.data.companyProjectGroupId);
	const runningGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.RUNNING);
	const planningGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.PLANNING);
	const opportunityGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.OPPORTUNITY);
	const haltedGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.HALTED);
	const doneGroups = projectGroups.filter(group => group.data.status === PROJECT_STATUS.DONE);
	const projectGroupGroups = groups.filter(group => group.data.companyProjectGroupId);
	const getStatusPriority = status => {
		switch (status) {
			case PROJECT_STATUS.RUNNING:
				return 0;
			case PROJECT_STATUS.PLANNING:
				return 1;
			case PROJECT_STATUS.OPPORTUNITY:
				return 2;
			case PROJECT_STATUS.HALTED:
				return 3;
			case PROJECT_STATUS.DONE:
				return 4;
			default:
				// eslint-disable-next-line no-console
				console.warn('Unrecognized project status: ' + status);
				return 50;
		}
	};

	for (const group of projectGroupGroups) {
		const projects = pageComponent.getData().projects.filter(project => project.projectGroupId === group.id);
		let highestStatusPriority = 99;
		for (const project of projects) {
			const statusPriority = getStatusPriority(project.status);
			if (statusPriority < highestStatusPriority) {
				highestStatusPriority = statusPriority;
			}
		}
		switch (highestStatusPriority) {
			case 0:
				runningGroups.push(group);
				break;
			case 1:
				planningGroups.push(group);
				break;
			case 2:
				opportunityGroups.push(group);
				break;
			case 3:
				haltedGroups.push(group);
				break;
			case 4:
				doneGroups.push(group);
				break;
		}
	}
	const getProjectGroupDates = projectGroupId => {
		let startDate = null;
		let endDate = null;
		for (const project of pageComponent.getData().projects.filter(project => project.projectGroupId === projectGroupId)) {
			const projectStartDate = project.projectStartDay ? Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay) : null;
			const projectEndDate = project.projectEndDay ? Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay).endOf('day') : null;
			if (projectStartDate && (!startDate || startDate.isAfter(projectStartDate))) {
				startDate = projectStartDate;
			}
			if (projectEndDate && (!endDate || endDate.isBefore(projectEndDate))) {
				endDate = projectEndDate;
			}
		}
		return {startDate, endDate};
	};
	const getProjectDates = projectId => {
		const project = pageComponent.getData().projects.find(project => project.id === projectId);
		const startDate = project.projectStartDay ? Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay) : null;
		const endDate = project.projectEndDay ? Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay).endOf('day') : null;
		return {startDate, endDate};
	};

	for (const group of [runningGroups, planningGroups, opportunityGroups, haltedGroups, doneGroups]) {
		group.sort((a, b) => {
			const aDates = a.data.companyProjectGroupId ? getProjectGroupDates(a.id) : getProjectDates(a.id);
			const bDates = b.data.companyProjectGroupId ? getProjectGroupDates(b.id) : getProjectDates(b.id);
			const aHasDates = aDates.startDate && aDates.endDate;
			const bHasDates = bDates.startDate && bDates.endDate;
			if (aHasDates && !bHasDates) return -1;
			if (bHasDates && !aHasDates) return 1;
			if (!aHasDates && !bHasDates) return 0;
			if (aDates.startDate && (!bDates.startDate || aDates.startDate.isBefore(bDates.startDate))) return -1;
			if (bDates.startDate && (!aDates.startDate || bDates.startDate.isBefore(aDates.startDate))) return 1;
			return 0;
		});
	}
	return runningGroups.concat(planningGroups).concat(opportunityGroups).concat(haltedGroups).concat(doneGroups);
};

export const composeTaskItem = (pageComponent, task, simulationMode = false) => {
	if (task.startYear && task.deadlineYear) {
		const sharedContext = pageComponent.props.sharedContext;
		const startDate = createCanvasTimelineDate(task.startYear, task.startMonth, task.startDay);
		const endDate = createCanvasTimelineDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);
		if (!areItemDatesValid(startDate, endDate)) return null;
		const project = pageComponent.state.data.projects.find(project => project.id === task.projectId);
		return {
			groupId: task.id,
			startDate,
			endDate,
			draggableType: ['DONE', 'HALTED'].includes(project.status) || sharedContext ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
			color: project.projectColor,
			task,
			projectId: project.id,
			phaseId: task.phaseId,
			onMoveAttempt: ['DONE', 'HALTED'].includes(project.status) ? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent) : undefined,
			onMouseEnter: sharedContext ? undefined : pageComponent.onTaskItemMouseEnter.bind(pageComponent, task),
			onMouseMove: sharedContext ? undefined : pageComponent.onTaskItemMouseMove.bind(pageComponent),
			onMouseLeave: sharedContext ? undefined : pageComponent.onTaskItemMouseLeave.bind(pageComponent, task),
			onMoving: sharedContext ? undefined : pageComponent.onTaskItemMoving.bind(pageComponent),
			onMoveEnd: sharedContext ? undefined : pageComponent.onTaskItemMoveEnd.bind(pageComponent),
			allowDependencyCreation: !simulationMode && !project.isJiraProject && !sharedContext,
			onClick: sharedContext
				? null
				: (taskItem, e) => {
						const pictureStartX = taskItem.x + taskItem.width + GROUP_SECTION_WIDTH;
						const pictureEndX = taskItem.x + taskItem.width + GROUP_SECTION_WIDTH + AUTO_SCHEDULING_PERSON_WIDTH;

						if (e.clientX >= pictureStartX && e.clientX <= pictureEndX) {
							const tasksInPeriod = pageComponent.visibleTaskData.filter(visibleTask => {
								const task = taskItem.data.task;
								const taskStartDate = createCanvasTimelineDate(task.startYear, task.startMonth, task.startDay);
								const taskEndDate = createCanvasTimelineDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);

								const visibleTaskStartDate = createCanvasTimelineDate(visibleTask.task.startYear, visibleTask.task.startMonth, visibleTask.task.startDay);
								const visibleTaskEndDate = createCanvasTimelineDate(visibleTask.task.deadlineYear, visibleTask.task.deadlineMonth, visibleTask.task.deadlineDay);

								const personMatch = visibleTask.task.autoSchedulingPerson && task.autoSchedulingPerson && visibleTask.task.autoSchedulingPerson.id === task.autoSchedulingPerson.id;
								const datesOverlap = !(taskEndDate < visibleTaskStartDate || taskStartDate > visibleTaskEndDate);

								return personMatch && datesOverlap && task.id !== visibleTask.task.id;
							});

							if (taskItem.data.task.autoSchedulingPerson) {
								pageComponent.timelineHeatmapCache.delete(taskItem.data.task.autoSchedulingPerson.id);
							}
							taskItem.data.task.autoSchedulingPerson = null;
							taskItem.horizontalExtension = 0;
							pageComponent.setState({assignedPersonHoverData: null});
							dispatch(EVENT_ID.CANVAS_TIMELINE_FORCE_REDRAW, {preventFiltering: false});

							if (tasksInPeriod.length > 0) {
								pageComponent.runPersonUtilizationWebWorker();
							}

							return;
						}
						pageComponent.props.showTaskModal(task.companyTaskId);
				  },
			onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
			onDraw: pageComponent.onTaskItemDraw.bind(pageComponent, task),
		};
	}
};

export const composeTaskGroup = (pageComponent, project, task, phaseId, parentTaskToSubtaskMap, subTaskLevel) => {
	const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
	const sharedContext = pageComponent.props.sharedContext;
	const subTaskGroups = [];
	const taskId = task.id;
	if (parentTaskToSubtaskMap.has(taskId)) {
		const subTaskIds = parentTaskToSubtaskMap.get(taskId);
		for (const subTaskId of subTaskIds) {
			const subTask = pageComponent.state.data.taskMap.get(subTaskId);
			if (subTask) {
				subTaskGroups.push(new TaskGroup(composeTaskGroup(pageComponent, project, subTask, phaseId, parentTaskToSubtaskMap, subTaskLevel + 1)));
			}
		}
	}

	const expandable = subTaskGroups.length > 0;

	return {
		task,
		id: task.id,
		companyTaskId: task.companyTaskId,
		name: task.name,
		color: project.projectColor,
		isDone: task.done,
		isStarted: pageComponent.isTaskStarted(task.statusColumnId),
		isInProjectGroup: project.isInProjectGroup,
		hideIfEmpty: !isSimulationMode(),
		forceDrawCondition: () => {
			return getRelevantDependencyTaskIdSet(pageComponent).has(task.id);
		},
		onClick: sharedContext
			? null
			: () => {
					pageComponent.props.showTaskModal(task.companyTaskId);
			  },
		onItemCreate:
			isDoneOrHalted || sharedContext
				? undefined
				: (startDate, endDate, group) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							selectedPhaseId: phaseId,
							estimationUnit: project.estimationUnit,
							roles: pageComponent.getData().roles,
							persons: pageComponent.getData().persons,
							projects: pageComponent.getData().projects,
							phases: pageComponent.getData().phases,
							projectPersons: pageComponent.getData().projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: pageComponent.getData().viewer.actualPersonId,
							removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
		expandable: expandable,
		subTaskLevel: subTaskLevel,
		groups: subTaskGroups,
	};
};

export const composePhaseGroup = (pageComponent, phase, phaseId, phaseIdToTaskIdsMap, taskIdToTaskMap, parentTaskToSubtaskMap) => {
	const project = pageComponent.state.data.projects.find(project => project.id === phase.projectId);
	const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
	const sharedContext = pageComponent.props.sharedContext;
	const props = pageComponent.props;
	const {formatMessage} = props.intl;

	let phaseTimeRegistered = 0;
	let phaseTimeRegisteredNotDone = 0;
	let phaseRemaining = 0;
	let phaseForecast = 0;
	let phaseDoneForecast = 0;
	let phaseTotalTasks = 0;
	let phaseDoneTasks = 0;

	const taskGroups = [];
	if (phaseIdToTaskIdsMap.has(phaseId)) {
		for (const taskId of phaseIdToTaskIdsMap.get(phaseId)) {
			const task = taskIdToTaskMap.get(taskId);
			++phaseTotalTasks;
			if (task.done) {
				++phaseDoneTasks;
			}
			phaseTimeRegistered += (pageComponent.state.data.parentTimeRegistrationMap.get(task.id) || []).reduce((total, timeRegistration) => total + timeRegistration.minutesRegistered, 0);
			if (!task.done) {
				phaseTimeRegisteredNotDone += (pageComponent.state.data.parentTimeRegistrationMap.get(task.id) || []).reduce((total, timeRegistration) => total + timeRegistration.minutesRegistered, 0);
			}
			phaseRemaining += task.timeLeft;
			phaseForecast += task.estimateForecast;
			if (task.done) {
				phaseDoneForecast += task.estimateForecast;
			}
			taskGroups.push(new TaskGroup(composeTaskGroup(pageComponent, project, task, phaseId, parentTaskToSubtaskMap, 0)));
		}
	}
	taskGroups.push(
		new PlaceholderGroup({
			id: phaseId + 'empty',
			name: props.intl.formatMessage({id: 'scheduling.no_current_tasks'}),
			xOffset: project.isInProjectGroup ? 16 : 10,
			showOnlyIfNoSiblingsVisible: true,
			onItemCreate:
				isDoneOrHalted || sharedContext
					? undefined
					: (startDate, endDate, group) => {
							showModal({
								type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
								selectedProjectId: project.id,
								selectedPhaseId: phaseId,
								estimationUnit: project.estimationUnit,
								roles: pageComponent.getData().roles,
								persons: pageComponent.getData().persons,
								projects: pageComponent.getData().projects,
								phases: pageComponent.getData().phases,
								projectPersons: pageComponent.getData().projectPersons,
								startDate: getMomentFromCanvasTimelineDate(startDate),
								endDate: getMomentFromCanvasTimelineDate(endDate),
								isProjectScheduling: true,
								viewerId: pageComponent.getData().viewer.actualPersonId,
								removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
							});
					  },
		})
	);
	let phaseGroupData = null;

	const isMilestoneNotAutoscheduled = isSimulationMode() && pageComponent.getData() && pageComponent.getData().autoScheduleProposalData ? pageComponent.getData().autoScheduleProposalData.excludedMilestonesIds.includes(phase.id) : false;
	phaseGroupData = {
		phase,
		id: phase.id,
		name: `${phase.name} ${isMilestoneNotAutoscheduled ? `(${formatMessage({id: 'auto_scheduling.not_autoscheduled'})})` : ''}`,
		faded: isMilestoneNotAutoscheduled,
		color: project.projectColor,
		isInProjectGroup: project.isInProjectGroup,
		groups: taskGroups,
		onItemCreate:
			isDoneOrHalted || sharedContext
				? undefined
				: (startDate, endDate, group) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							selectedPhaseId: phase.id,
							estimationUnit: project.estimationUnit,
							roles: pageComponent.getData().roles,
							persons: pageComponent.getData().persons,
							projects: pageComponent.getData().projects,
							phases: pageComponent.getData().phases,
							projectPersons: pageComponent.getData().projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							viewerId: pageComponent.getData().viewer.actualPersonId,
							removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
	};

	return {
		phaseTimeRegistered,
		phaseTimeRegisteredNotDone,
		phaseRemaining,
		phaseForecast,
		phaseDoneForecast,
		phaseTotalTasks,
		phaseDoneTasks,
		phaseGroupData,
	};
};

function setPhaseDatesFromProject(phase, project) {
	// phases with no dates inherit from project start and end dates
	if (!phase.startYear) {
		phase.startYear = project.projectStartYear;
		phase.startMonth = project.projectStartMonth;
		phase.startDay = project.projectStartDay;
	}

	if (!phase.deadlineYear) {
		phase.deadlineYear = project.projectEndYear;
		phase.deadlineMonth = project.projectEndMonth;
		phase.deadlineDay = project.projectEndDay;
	}
}

export const composePhaseItem = (pageComponent, phase, phaseTimeRegistered, phaseTimeRegisteredNotDone, phaseRemaining, phaseForecast, phaseDoneForecast, phaseTotalTasks, phaseDoneTasks) => {
	const project = pageComponent.state.data.projects.find(project => project.id === phase.projectId);
	const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
	const sharedContext = pageComponent.props.sharedContext;

	setPhaseDatesFromProject(phase, project);
	//adding phase items
	if (phase.startYear && phase.deadlineYear) {
		const startDate = createCanvasTimelineDate(phase.startYear, phase.startMonth, phase.startDay);
		const endDate = createCanvasTimelineDate(phase.deadlineYear, phase.deadlineMonth, phase.deadlineDay);
		if (!areItemDatesValid(startDate, endDate)) return null;
		let completion;
		if (pageComponent.state.data.viewer.availableFeatureFlags.find(flag => flag.key === 'use_new_progress_calc')) {
			completion = Util.calculateTaskGroupCompletion(phaseDoneForecast, phaseTimeRegisteredNotDone, phaseRemaining, phaseDoneTasks, phaseTotalTasks) * 100;
		} else {
			completion = Util.deprecated_calculateElementProgress(phaseForecast, phaseTimeRegistered, phaseRemaining, phaseTotalTasks, phaseDoneTasks, project.estimationUnit === ESTIMATION_UNIT.HOURS, project.minutesPerEstimationPoint, false, false);
		}
		return {
			groupId: phase.id,
			phaseName: phase.name,
			startDate,
			endDate,
			onMoving: pageComponent.onPhaseItemMoving.bind(pageComponent),
			onMoveEnd: pageComponent.onPhaseItemMoveEnd.bind(pageComponent),
			onMoveAttempt: isDoneOrHalted ? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent) : undefined,
			draggableType: !hasPermission(PERMISSION_TYPE.MILESTONE_UPDATE) || sharedContext || isDoneOrHalted ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
			color: project.projectColor,
			completion,
			projectId: project.id,
			isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
			onMouseEnter: sharedContext ? undefined : pageComponent.onDistributionItemMouseEnter.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PHASE, project.id, phase.id),
			onMouseLeave: sharedContext ? undefined : pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
			onMouseMove: sharedContext ? undefined : pageComponent.onDistributionItemMouseMove.bind(pageComponent),
			onClick: (project.useManualAllocations && project.isInProjectGroup) || sharedContext ? undefined : pageComponent.onDistributionItemClick.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PHASE, project.id, phase.id),
		};
	}
};
export const composeProjectAllocation = (pageComponent, allocation) => {
	const data = pageComponent.getData();
	const props = pageComponent.props;
	const sharedContext = props.sharedContext;
	const startDate = createCanvasTimelineDate(allocation.startYear, allocation.startMonth, allocation.startDay);
	const endDate = createCanvasTimelineDate(allocation.endYear, allocation.endMonth, allocation.endDay);
	if (!areItemDatesValid(startDate, endDate)) return null;
	const totalHours = getAllocationTotal(allocation) / 60;
	let isDoneOrHalted = false;
	if (allocation.projectId && ['DONE', 'HALTED'].includes(getProjectById(allocation.projectId, pageComponent.getFilterData()).status)) {
		isDoneOrHalted = true;
	}

	const groupId = allocation.projectId ? `${allocation.projectId}-${allocation.personId}` : allocation.projectGroupId ? `${allocation.projectGroupId}-${allocation.personId}` : `${allocation.personId}-2`;
	const color = allocation.projectId ? data.projects.find(project => project.id === allocation.projectId).projectColor : allocation.projectGroupId ? allocation.projectGroupColor : '#efefef';

	return {
		groupId,
		startDate,
		endDate,
		color,
		totalHours,
		intl: props.intl,
		allocation,
		onMoveAttempt: isDoneOrHalted ? pageComponent.onDoneOrHaltedMoveAttempt.bind(pageComponent) : undefined,
		draggableType: !hasPermission(PERMISSION_TYPE.ALLOCATION_UPDATE) || isDoneOrHalted || sharedContext ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
		onMoving: sharedContext ? undefined : pageComponent.onAllocationItemMoving.bind(pageComponent),
		onMoveEnd: sharedContext ? undefined : pageComponent.onAllocationItemMoveEnd.bind(pageComponent),
		onMouseEnter: sharedContext ? undefined : pageComponent.onAllocationMouseEnter.bind(pageComponent, allocation, null, null),
		onMouseLeave: sharedContext ? undefined : pageComponent.onAllocationMouseLeave.bind(pageComponent),
		onMouseMove: sharedContext ? undefined : pageComponent.onAllocationMouseMove.bind(pageComponent),
		onClick:
			!hasPermission(PERMISSION_TYPE.ALLOCATION_UPDATE) || isDoneOrHalted || sharedContext
				? undefined
				: () => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE,
							projectGroups: pageComponent.getData().projectGroups,
							projects: pageComponent.getData().projects,
							idleTimes: pageComponent.getData().idleTimes,
							projectPersons: pageComponent.getData().projectPersons,
							actualPersonId: pageComponent.getData().viewer.actualPersonId,
							persons: pageComponent.getData().persons,
							selectedPersonId: allocation.personId,
							milestones: pageComponent.getData().phases,
							company: pageComponent.getData().company,
							holidayCalendars: pageComponent.getData().holidayCalendars,
							holidayCalendarEntries: pageComponent.getData().holidayCalendarEntries,
							allocation: allocation,
							roles: pageComponent.getData().roles,
							teams: pageComponent.getData().teams,
							teamPersons: pageComponent.getData().teamPersons,
							removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
	};
};

export const composeProject = (pageComponent, data, props, project, items, groups, projectGroupMaps, projectGroupHourEstMap, projectGroupTeamPicturesMap) => {
	const sharedContext = pageComponent.props.sharedContext;
	const {formatMessage} = props.intl;
	const {projectIdToPhaseIdsMap, phaseIdToPhaseMap} = getPhasesMap(data);
	const {phaseIdToTaskIdsMap, taskIdToTaskMap, parentTaskToSubtaskMap} = data;
	const {projectIdToProjectPersonMap, projectIdToPersonsNotInTeamMap} = getProjectPersonsMap(data);
	const {personIdToPersonMap} = getPersonsMap(data);
	const {clientIdToClientMap} = getClientsMap(data);
	const {roleIdToRoleMap} = getRolesMap(data);
	let projectStartDate = null;
	let projectEndDate = null;
	const isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
	if (project.projectStartYear && project.projectEndYear) {
		//adding project items
		projectStartDate = Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay);
		projectEndDate = Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);
		const startDate = getCanvasTimelineDateFromMoment(projectStartDate);
		const endDate = getCanvasTimelineDateFromMoment(projectEndDate);
		if (areItemDatesValid(startDate, endDate)) {
			items.push(
				new ProjectItem({
					groupId: project.id,
					startDate,
					endDate,
					draggableType: !hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE) || isDoneOrHalted || sharedContext ? undefined : DRAGGABLE_TYPE.DATE_ONLY,
					color: project.projectColor,
					project,
					isHourEstimated: project.estimationUnit === ESTIMATION_UNIT.HOURS,
					isPlanning: project.status === PROJECT_STATUS.PLANNING || project.status === PROJECT_STATUS.OPPORTUNITY,
					intl: props.intl,
					planningText: project.status === PROJECT_STATUS.OPPORTUNITY ? formatMessage({id: 'project_status.opportunity'}) : formatMessage({id: 'project_status.planning'}),
					projectGroupId: project.projectGroupId,
					onMoving: sharedContext ? undefined : pageComponent.onProjectItemMoving.bind(pageComponent),
					onMoveEnd: sharedContext ? undefined : pageComponent.onProjectItemMoveEnd.bind(pageComponent),
					onMouseEnter: sharedContext ? undefined : pageComponent.onDistributionItemMouseEnter.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PROJECT, project.projectGroupId || project.id, project.projectGroupId ? project.id : null),
					onMouseLeave: sharedContext ? undefined : pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
					onMouseMove: sharedContext ? undefined : pageComponent.onDistributionItemMouseMove.bind(pageComponent),
					onClick: sharedContext ? undefined : pageComponent.onDistributionItemClick.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PROJECT, project.projectGroupId || project.id, project.projectGroupId ? project.id : null),
				})
			);
		}
		//check if project group start/end date maps need to be updated
		if (project.isInProjectGroup && projectGroupMaps.projectGroupStartDateMap.has(project.projectGroupId) && projectGroupMaps.projectGroupEndDateMap.has(project.projectGroupId)) {
			const projectGroupStartDate = projectGroupMaps.projectGroupStartDateMap.get(project.projectGroupId),
				projectGroupEndDate = projectGroupMaps.projectGroupEndDateMap.get(project.projectGroupId);
			if (projectGroupStartDate === null || projectStartDate.isBefore(projectGroupStartDate)) {
				projectGroupMaps.projectGroupStartDateMap.set(project.projectGroupId, projectStartDate.clone());
			}
			if (projectGroupEndDate === null || projectEndDate.isAfter(projectGroupEndDate)) {
				projectGroupMaps.projectGroupEndDateMap.set(project.projectGroupId, projectEndDate.clone());
			}
		}
	}
	//set timeLeft and estimateForecast maps
	if (project.isInProjectGroup) {
		projectGroupHourEstMap.set(project.projectGroupId, project.estimationUnit);
		if (projectGroupMaps.projectGroupTimeLeftMap.has(project.projectGroupId)) {
			let projectGroupTimeLeft = projectGroupMaps.projectGroupTimeLeftMap.get(project.projectGroupId);
			projectGroupTimeLeft += project.remaining;
			projectGroupMaps.projectGroupTimeLeftMap.set(project.projectGroupId, projectGroupTimeLeft);
		}
		if (projectGroupMaps.projectGroupEstimateForecastMap.has(project.projectGroupId)) {
			let projectGroupEstimateForecast = projectGroupMaps.projectGroupEstimateForecastMap.get(project.projectGroupId);
			projectGroupEstimateForecast += project.forecast;
			projectGroupMaps.projectGroupEstimateForecastMap.set(project.projectGroupId, projectGroupEstimateForecast);
		}
	}

	const phaseGroups = [];
	const projectSubGroups = [];
	if (projectIdToPhaseIdsMap.has(project.id)) {
		for (const phaseId of projectIdToPhaseIdsMap.get(project.id)) {
			if (phaseIdToPhaseMap.has(phaseId)) {
				const phase = phaseIdToPhaseMap.get(phaseId);

				const {phaseTimeRegistered, phaseTimeRegisteredNotDone, phaseRemaining, phaseForecast, phaseDoneForecast, phaseTotalTasks, phaseDoneTasks, phaseGroupData} = composePhaseGroup(
					pageComponent,
					phase,
					phaseId,
					phaseIdToTaskIdsMap,
					taskIdToTaskMap,
					parentTaskToSubtaskMap
				);
				if (phaseGroupData) {
					phaseGroups.push(new PhaseGroup(phaseGroupData));
				}
				const phaseItemData = composePhaseItem(pageComponent, phase, phaseTimeRegistered, phaseTimeRegisteredNotDone, phaseRemaining, phaseForecast, phaseDoneForecast, phaseTotalTasks, phaseDoneTasks);
				if (phaseItemData) {
					items.push(new PhaseItem(phaseItemData));
				}
			}
		}
	}
	pageComponent.sortPhaseGroupsByDate(phaseGroups);

	//tasks without milestone are mapped by project id. If map has project id key it means there are tasks that dont have milestone
	if (phaseIdToTaskIdsMap.has(project.id)) {
		const noMilestoneTaskGroups = [];
		for (const taskId of phaseIdToTaskIdsMap.get(project.id)) {
			const task = taskIdToTaskMap.get(taskId);
			noMilestoneTaskGroups.push(new TaskGroup(composeTaskGroup(pageComponent, project, task, null, parentTaskToSubtaskMap)));
		}
		noMilestoneTaskGroups.push(
			new PlaceholderGroup({
				id: project.id + 'no_milestoneempty',
				name: props.intl.formatMessage({id: 'scheduling.no_current_tasks'}),
				xOffset: project.isInProjectGroup ? 16 : 10,
				showOnlyIfNoSiblingsVisible: true,
				onItemCreate: sharedContext
					? null
					: (startDate, endDate, group) => {
							showModal({
								type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
								selectedProjectId: project.id,
								estimationUnit: project.estimationUnit,
								roles: pageComponent.getData().roles,
								persons: pageComponent.getData().persons,
								projects: pageComponent.getData().projects,
								phases: pageComponent.getData().phases,
								projectPersons: pageComponent.getData().projectPersons,
								startDate: getMomentFromCanvasTimelineDate(startDate),
								endDate: getMomentFromCanvasTimelineDate(endDate),
								isProjectScheduling: true,
								viewerId: pageComponent.getData().viewer.actualPersonId,
								removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
							});
					  },
			})
		);

		const isNoMilestoneNotAutoscheduled =
			isSimulationMode() && pageComponent.getData() && pageComponent.getData().autoScheduleProposalData ? pageComponent.getData().autoScheduleProposalData.excludedMilestonesIds.some(id => +atob(id).replace('PhaseType:', '') === -1) : false;
		phaseGroups.push(
			new PhaseGroup({
				id: project.id + 'no_milestone',
				name: `${formatMessage({id: 'project_scopes.no-scope'})} ${isNoMilestoneNotAutoscheduled ? `(${formatMessage({id: 'auto_scheduling.not_autoscheduled'})})` : ''}`,
				color: project.projectColor,
				faded: isNoMilestoneNotAutoscheduled,
				isInProjectGroup: project.isInProjectGroup,
				groups: noMilestoneTaskGroups,
				onItemCreate:
					isDoneOrHalted || sharedContext
						? undefined
						: (startDate, endDate, group) => {
								showModal({
									type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
									selectedProjectId: project.id,
									estimationUnit: project.estimationUnit,
									roles: pageComponent.getData().roles,
									persons: pageComponent.getData().persons,
									projects: pageComponent.getData().projects,
									phases: pageComponent.getData().phases,
									projectPersons: pageComponent.getData().projectPersons,
									startDate: getMomentFromCanvasTimelineDate(startDate),
									endDate: getMomentFromCanvasTimelineDate(endDate),
									isProjectScheduling: true,
									viewerId: pageComponent.getData().viewer.actualPersonId,
									removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
								});
						  },
			})
		);
	}
	const phasesSubGroup = new ProjectSubGroup({
		id: project.id + 'phases',
		name: formatMessage({id: 'common.milestones'}),
		color: project.projectColor,
		isInProjectGroup: project.isInProjectGroup,
		projectGroupColor: project.isInProjectGroup ? projectGroupMaps.projectGroupColorMap.get(project.projectGroupId) : null,
		phases: data.phases.filter(phase => phase.projectId === project.id),
		groups: phaseGroups,
		onMouseEnter: pageComponent.onPhasesGroupMouseEnter.bind(pageComponent, projectIdToPhaseIdsMap.get(project.id)),
		onMouseLeave: pageComponent.onPhasesGroupMouseLeave.bind(pageComponent),
		onItemCreate:
			isDoneOrHalted || sharedContext || !hasPermission(PERMISSION_TYPE.MILESTONE_CREATE)
				? undefined
				: (startDate, endDate, group) => {
						showModal({
							type: MODAL_TYPE.CANVAS_CREATE_NEW_MODAL,
							selectedProjectId: project.id,
							estimationUnit: project.estimationUnit,
							roles: pageComponent.getData().roles,
							persons: pageComponent.getData().persons,
							projects: pageComponent.getData().projects,
							phases: pageComponent.getData().phases,
							projectPersons: pageComponent.getData().projectPersons,
							startDate: getMomentFromCanvasTimelineDate(startDate),
							endDate: getMomentFromCanvasTimelineDate(endDate),
							isProjectScheduling: true,
							isMilestoneTabSelected: true,
							viewerId: pageComponent.getData().viewer.actualPersonId,
							removeLowHigh: pageComponent.getData().viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
						});
				  },
	});
	projectSubGroups.push(phasesSubGroup);
	const teamMemberGroups = [],
		teamProfilePictureSrcs = [];

	let projectGroupContainsOnlyHaltedOrDoneProjects;
	let color = project.projectColor;
	if (project.isInProjectGroup) {
		color = data.projectGroups.find(projectGroup => projectGroup.id === project.projectGroupId).color;
		// check if the group contains only projects that are halted or done
		const projectGroupId = project.projectGroupId;
		projectGroupContainsOnlyHaltedOrDoneProjects = data.projects
			.filter(project => {
				return project.projectGroupId === projectGroupId;
			})
			.every(project => {
				return ['HALTED', 'DONE'].includes(project.status);
			});
	}

	if (projectIdToProjectPersonMap.has(project.id)) {
		for (const projectPerson of projectIdToProjectPersonMap.get(project.id)) {
			const person = personIdToPersonMap.get(projectPerson.personId);
			//Don't include client users
			if (person.clientId) continue;
			const roleName = roleIdToRoleMap.has(projectPerson.roleId) ? roleIdToRoleMap.get(projectPerson.roleId).name : '';

			teamMemberGroups.push(pageComponent.createPersonGroup(person, project, projectPerson.roleId, roleName, (project.projectGroupId || project.id) + '-' + person.id, color, projectGroupContainsOnlyHaltedOrDoneProjects));
			const imgObj = {
				personId: person.id,
				profilePictureId: person.profilePictureId,
				profilePictureDefaultId: person.profilePictureDefaultId,
				initials: person.initials,
				fullName: person.fullName,
			};
			teamProfilePictureSrcs.push(imgObj);
		}
	}
	if (projectIdToPersonsNotInTeamMap.has(project.id)) {
		for (const person of projectIdToPersonsNotInTeamMap.get(project.id)) {
			//Don't include client users
			if (person.clientId) continue;
			const roleName = roleIdToRoleMap.has(person.roleId) ? roleIdToRoleMap.get(person.roleId).name : '';

			teamMemberGroups.push(pageComponent.createPersonGroup(person, project, person.roleId, roleName, (project.projectGroupId || project.id) + '-' + person.id, color, projectGroupContainsOnlyHaltedOrDoneProjects));
			const imgObj = {
				personId: person.id,
				profilePictureId: person.profilePictureId,
				profilePictureDefaultId: person.profilePictureDefaultId,
				initials: person.initials,
				fullName: person.fullName,
			};
			teamProfilePictureSrcs.push(imgObj);
		}
	}

	if (project.projectStartYear && project.projectEndYear) {
		if (project.isInProjectGroup) {
			projectGroupTeamPicturesMap.set(project.projectGroupId, teamProfilePictureSrcs);
		} else {
			const startDate = createCanvasTimelineDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay);
			const endDate = createCanvasTimelineDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);
			if (props.isProjectTimeline && areItemDatesValid(startDate, endDate)) {
				items.push(
					new ProjectTeamItem({
						groupId: project.id + 'team-members',
						projectId: project.id,
						startDate,
						endDate,
						color: project.projectColor,
						teamProfilePictureSrcs,
						onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
					})
				);
			}
		}
	}

	if (!sharedContext) {
		const showProjectTeam = (data.company.isUsingProjectAllocation || props.isProjectTimeline) && hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS);
		const teamMembersSubGroup = new ProjectSubGroup({
			id: (project.isInProjectGroup ? project.projectGroupId : project.id) + 'team-members',
			name: formatMessage({id: 'scheduling.project_scheduling.project_team'}),
			color: project.projectColor,
			projectGroupTeam: project.isInProjectGroup,
			projectGroupColor: project.isInProjectGroup ? projectGroupMaps.projectGroupColorMap.get(project.projectGroupId) : null,
			groups: showProjectTeam ? teamMemberGroups : undefined,
			count: teamProfilePictureSrcs.length,
			preventExpansion: sharedContext || !showProjectTeam,
		});
		if (project.isInProjectGroup) {
			projectGroupMaps.projectGroupTeamMap.set(project.projectGroupId, teamMembersSubGroup);
		} else {
			projectSubGroups.push(teamMembersSubGroup);
		}
	}
	const labelIds = data.projectLabels.filter(projectLabel => projectLabel.projectId === project.id).map(projectLabel => projectLabel.labelId);
	const projectGroup = new ProjectGroup({
		id: project.id,
		companyProjectId: project.companyProjectId,
		name: `${project.name}`,
		budgetType: project.budgetType,
		color: project.projectColor,
		status: project.status,
		clientId: project.clientId,
		clientName: project.clientId && clientIdToClientMap.has(project.clientId) ? clientIdToClientMap.get(project.clientId).name : '',
		isInProjectGroup: project.isInProjectGroup,
		projectGroupColor: project.isInProjectGroup ? projectGroupMaps.projectGroupColorMap.get(project.projectGroupId) : null,
		groups: projectSubGroups,
		hideIfEmpty: !props.isProjectTimeline,
		drawIfAnyChildVisible: true,
		labelIds,
		onMouseEnter: sharedContext ? undefined : pageComponent.onProjectGroupMouseEnter.bind(pageComponent, project),
		onMouseLeave: sharedContext ? undefined : pageComponent.onProjectGroupMouseLeave.bind(pageComponent, project),
	});
	if (project.isInProjectGroup) {
		if (projectGroupMaps.projectGroupTimelineGroups.has(project.projectGroupId)) {
			const projectGroupGroup = projectGroupMaps.projectGroupTimelineGroups.get(project.projectGroupId);
			const groups = projectGroupGroup.groups;
			groups.push(projectGroup);
			projectGroupGroup.groups = groups;
			projectGroupMaps.projectGroupTimelineGroups.set(project.projectGroupId, projectGroupGroup);
		}
	} else {
		groups.push(projectGroup);
	}
};

export const getGroupsAndItemsFromProps = (pageComponent, props, data) => {
	const {projectIdToProjectPersonMap} = getProjectPersonsMap(data);
	let groups = [];
	const items = [];
	const projectGroupHourEstMap = new Map(),
		projectGroupTeamPicturesMap = new Map();

	for (const allocation of data.allocations) {
		const allocationItemData = composeProjectAllocation(pageComponent, allocation);
		if (allocationItemData) {
			items.push(new ProjectAllocationItem(allocationItemData));
		}
	}

	for (const task of data.tasks) {
		if (task.startYear && task.deadlineYear) {
			const startDate = createCanvasTimelineDate(task.startYear, task.startMonth, task.startDay);
			const endDate = createCanvasTimelineDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);
			if (!areItemDatesValid(startDate, endDate)) continue;
			items.push(new TaskItem(composeTaskItem(pageComponent, task, isSimulationMode())));
		}
	}

	const projectEdges = data.projects.filter(project => {
		if (hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL)) {
			return true;
		}
		if (projectIdToProjectPersonMap.has(project.id)) {
			for (const projectPerson of projectIdToProjectPersonMap.get(project.id)) {
				if (projectPerson.personId === data.viewer.actualPersonId) {
					return true;
				}
			}
		}
		return false;
	});
	const projectGroupMaps = getProjectGroupMaps(pageComponent, data, props);
	/*
	 * map through all projects.
	 * single projects should be added to array of returned timeline groups
	 * projects that belong to project group should be added to map that later will be casted into array of timeline groups for project group
	 */
	for (const project of projectEdges) {
		composeProject(pageComponent, data, props, project, items, groups, projectGroupMaps, projectGroupHourEstMap, projectGroupTeamPicturesMap);
	}
	projectGroupMaps.projectGroupTimelineGroups.forEach((value, key, map) => {
		//if project group is empty it means viewer was not assigned to any of its projects. Dont add these groups to the array
		if (value.groups.length !== 0) {
			if (projectGroupMaps.projectGroupTeamMap.has(key)) {
				const teamSubGroup = projectGroupMaps.projectGroupTeamMap.get(key);
				if (teamSubGroup) {
					teamSubGroup.data.color = value.data.color;
					value.groups.push(teamSubGroup);
					//add timeline iteam for each group
					if (projectGroupMaps.projectGroupStartDateMap.get(key) !== null && projectGroupMaps.projectGroupEndDateMap.get(key) !== null) {
						items.push(
							new ProjectItem({
								groupId: key,
								intl: props.intl,
								startDate: getCanvasTimelineDateFromMoment(projectGroupMaps.projectGroupStartDateMap.get(key)),
								endDate: getCanvasTimelineDateFromMoment(projectGroupMaps.projectGroupEndDateMap.get(key)),
								color: value.data.color,
								projects: data.projects.filter(project => project.projectGroupId === key),
								isHourEstimated: projectGroupHourEstMap.get(key) === ESTIMATION_UNIT.HOURS,
								onMouseEnter: pageComponent.onDistributionItemMouseEnter.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PROJECT, key, null),
								onMouseLeave: pageComponent.onDistributionItemMouseLeave.bind(pageComponent),
								onMouseMove: pageComponent.onDistributionItemMouseMove.bind(pageComponent),
								onClick: pageComponent.onDistributionItemClick.bind(pageComponent, ITEM_TYPE.PROJECT_SCHEDULING_PROJECT, key, null),
								useNewProgressCalculation: data.viewer.availableFeatureFlags.find(flag => flag.key === 'use_new_progress_calc'),
							})
						);
						if (props.isProjectTimeline) {
							items.push(
								new ProjectTeamItem({
									groupId: key + 'team-members',
									startDate: getCanvasTimelineDateFromMoment(projectGroupMaps.projectGroupStartDateMap.get(key)),
									endDate: getCanvasTimelineDateFromMoment(projectGroupMaps.projectGroupEndDateMap.get(key)),
									color: value.data.color,
									teamProfilePictureSrcs: projectGroupTeamPicturesMap.get(key),
									onPersonImageLoad: pageComponent.onPersonImageLoad.bind(pageComponent),
								})
							);
						}
					}
				}
			}
			groups.push(value);
		}
	});
	groups = sortProjectGroups(pageComponent, groups);
	return {groups, items};
};
